import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { NamedLink, Page, LayoutComposer } from '../../components/index.js';
import TopbarSpacer from '../TopbarContainer/TopbarSpacer';
import FooterContainer from '../FooterContainer/FooterContainer.js';
import NotFoundPage from '../NotFoundPage/NotFoundPage.js';

import css from './EditorialsPage.module.css';

// Inline configuration for editorials
const EDITORIALS_CONFIG = {
  layout: {
    editorialsPage: {
      pageIds: [
        '20250607_home-for-icons_press',
        '20250616_loose-ends-004_editorial',
        '20250603_loose-ends-003_editorial',
        '20250602_loose-ends-002_editorial',
        '20250420_loose-ends-001_editorial',
        '20250502_scandinavian-mind_external',
      ],
      largeCardPageIds: [
        '20250607_home-for-icons_press',
        '20250502_scandinavian-mind_external',
      ],
    },
  },
};

// Get editorial page IDs from config or use defaults
const getEditorialPageIds = config => {
  const configuredIds = config?.layout?.editorialsPage?.pageIds;

  if (
    configuredIds &&
    Array.isArray(configuredIds) &&
    configuredIds.length > 0
  ) {
    return configuredIds;
  }

  // Default editorial page IDs
  return ['20250607_home-for-icons_press'];
};

// Get large card page IDs from config
const getLargeCardPageIds = config => {
  const configuredLargeIds = config?.layout?.editorialsPage?.largeCardPageIds;

  if (
    configuredLargeIds &&
    Array.isArray(configuredLargeIds) &&
    configuredLargeIds.length > 0
  ) {
    return configuredLargeIds;
  }

  // Default: first card is large
  return [];
};

// Helper function to extract featured image from page data
const getFeaturedImage = pageData => {
  const meta = pageData?.meta || {};

  // First, try to get the social sharing image
  // Check if the image is already denormalized (has url property)
  if (meta.socialSharing?.image?.url) {
    return meta.socialSharing.image.url;
  }

  // Check if we have variants in the socialSharing image (denormalized)
  if (meta.socialSharing?.image?.variants) {
    const variants = meta.socialSharing.image.variants;
    if (variants.social1200?.url) return variants.social1200.url;
    if (variants.social600?.url) return variants.social600.url;
    if (variants.original1200?.url) return variants.original1200.url;
    if (variants.original800?.url) return variants.original800.url;
    if (variants.original400?.url) return variants.original400.url;
  }

  // Check if socialSharing image has attributes.variants (denormalized asset structure)
  if (meta.socialSharing?.image?.attributes?.variants) {
    const variants = meta.socialSharing.image.attributes.variants;
    if (variants.social1200?.url) return variants.social1200.url;
    if (variants.social600?.url) return variants.social600.url;
    if (variants.original1200?.url) return variants.original1200.url;
    if (variants.original800?.url) return variants.original800.url;
    if (variants.original400?.url) return variants.original400.url;
  }

  // Fallback: look for the first image in sections
  const sections = pageData?.sections || [];
  for (const section of sections) {
    const blocks = section.blocks || [];
    for (const block of blocks) {
      // Check if the image is already denormalized
      if (block.media?.image?.url) {
        return block.media.image.url;
      }

      // Check if we have variants (denormalized)
      if (block.media?.image?.variants) {
        const variants = block.media.image.variants;
        if (variants.original800?.url) return variants.original800.url;
        if (variants.original400?.url) return variants.original400.url;
        if (variants.original1200?.url) return variants.original1200.url;
      }

      // Check if we have attributes.variants (denormalized asset structure)
      if (block.media?.image?.attributes?.variants) {
        const variants = block.media.image.attributes.variants;
        if (variants.original800?.url) return variants.original800.url;
        if (variants.original400?.url) return variants.original400.url;
        if (variants.original1200?.url) return variants.original1200.url;
      }
    }
  }

  return null;
};

// Helper function to extract title and description from page data
const getPageInfo = pageData => {
  const meta = pageData?.meta || {};
  const title = meta.pageTitle?.content || 'Untitled Editorial';
  const description = meta.pageDescription?.content || '';

  return { title, description };
};

// Helper function to check if page is external
const isExternalPage = pageId => {
  return pageId && pageId.endsWith('_external');
};

// Helper function to get external URL from page data
const getExternalUrl = pageData => {
  // Check if the first section has a callToAction with href
  const firstSection = pageData?.sections?.[0];
  if (firstSection?.callToAction?.href) {
    return firstSection.callToAction.href;
  }

  // Fallback: try meta fields (in case structure changes)
  const meta = pageData?.meta || {};
  return (
    meta.externalUrl || meta.redirectUrl || meta.targetUrl || meta.url || null
  );
};

// Helper function to extract category from page ID
const getCategoryFromPageId = pageId => {
  // If it's an external page, always return "Press"
  if (isExternalPage(pageId)) {
    return 'Press';
  }

  // Extract everything after the last underscore for internal pages
  const parts = pageId.split('_');
  if (parts.length > 1) {
    const category = parts[parts.length - 1];
    // Capitalize first letter only, keep rest lowercase
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
  }
  // Fallback to 'Editorial' if no underscore found
  return 'Editorial';
};

// Helper function to extract author and date info
const getPageDetails = (pageData, pageId) => {
  let author = '';
  let date = '';

  // First, try to extract date from page ID (YYYYMMDD format)
  if (pageId) {
    const dateMatch = pageId.match(/^(\d{8})/);
    if (dateMatch) {
      const dateStr = dateMatch[1];
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);

      // Format as YY.MM.DD
      const shortYear = year.substring(2);
      date = `${shortYear}.${month}.${day}`;
    }
  }

  return { author, date };
};

// Editorial Card Component
const EditorialCard = ({ pageId, pageData, className }) => {
  const featuredImage = getFeaturedImage(pageData);
  const { title, description } = getPageInfo(pageData);
  const { author, date } = getPageDetails(pageData, pageId);
  const category = getCategoryFromPageId(pageId);
  const isExternal = isExternalPage(pageId);
  const externalUrl = isExternal ? getExternalUrl(pageData) : null;

  const cardContent = (
    <>
      {featuredImage && (
        <div className={css.imageContainer}>
          <img src={featuredImage} alt={title} className={css.cardImage} />
        </div>
      )}
      <div className={css.cardContent}>
        <div className={css.cardMeta}>
          <span className={css.category}>{category}</span>
          {date && <span className={css.date}>{date}</span>}
        </div>
        <h1
          className={css.cardTitle}
          dangerouslySetInnerHTML={{
            __html: title.replace(/\n/g, '<br>'),
          }}
        />
        {description && (
          <p
            className={css.cardDescription}
            dangerouslySetInnerHTML={{
              __html: description.replace(/\n/g, '<br>'),
            }}
          />
        )}
        {author && (
          <div className={css.cardFooter}>
            <span className={css.author}>{author}</span>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className={className}>
      {isExternal && externalUrl ? (
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={css.cardLink}
        >
          {cardContent}
        </a>
      ) : (
        <NamedLink
          name="CMSPage"
          params={{ pageId }}
          to={{ search: '?from=editorials' }}
          className={css.cardLink}
        >
          {cardContent}
        </NamedLink>
      )}
    </div>
  );
};

// Main Editorial Grid Component
const EditorialGrid = ({ pageAssetsData, config }) => {
  const configToUse = config || EDITORIALS_CONFIG;
  const editorialPageIds = getEditorialPageIds(configToUse);
  const largeCardPageIds = getLargeCardPageIds(configToUse);

  if (!pageAssetsData) {
    return <div className={css.loading}>Loading news...</div>;
  }

  // Only show editorials that are in our configured list and successfully loaded
  const editorialEntries = editorialPageIds
    .map(pageId => ({
      pageId,
      assetData: pageAssetsData[pageId],
    }))
    .filter(({ assetData }) => assetData?.data) // Only include successfully loaded pages
    .map(({ pageId, assetData }) => ({
      pageId,
      pageData: assetData.data,
    }));

  if (editorialEntries.length === 0) {
    return <div className={css.noContent}>No News available.</div>;
  }

  return (
    <div className={css.editorialGrid}>
      {editorialEntries.map(({ pageId, pageData }, index) => {
        // Check if this page ID should be displayed as a large card
        const isLargeCard = largeCardPageIds.includes(pageId);
        let cardClass = css.editorialCard;

        if (isLargeCard) {
          cardClass = `${css.editorialCard} ${css.largeCard}`;
        }

        return (
          <EditorialCard
            key={pageId}
            pageId={pageId}
            pageData={pageData}
            className={cardClass}
          />
        );
      })}
    </div>
  );
};

// Pagination Component (placeholder for now)
const EditorialPagination = () => {
  return (
    <div className={css.pagination}>
      <span className={css.paginationInfo}>1, 2, 3... 7, 8, 9 → 15</span>
      <div className={css.paginationControls}>
        <button className={css.paginationButton}>Previous</button>
        <button className={css.paginationButton}>Next</button>
      </div>
    </div>
  );
};

// Main Editorials Page Component
export const EditorialsPageComponent = props => {
  const { pageAssetsData, inProgress, error, config } = props;

  if (!inProgress && error?.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  // Create metadata for the editorials page
  const pageMetadata = {
    title: 'News - Used Icons',
    description:
      'Discover inspiring stories and insights from the world of Used Icons.',
    schema: {
      '@context': 'http://schema.org',
      '@type': 'CollectionPage',
      name: 'News',
      description: 'A collection of news articles about design and furniture.',
    },
    socialSharing: {
      title: 'News - Used Icons',
      description:
        'Discover inspiring stories and insights from the world of Used Icons.',
    },
  };

  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <Page {...pageMetadata}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <div className={css.wrapper}>
                <Topbar as="header" className={css.topbar}>
                  <TopbarSpacer />
                </Topbar>
                <Main as="main" className={css.main}>
                  <div className={css.container}>
                    <header className={css.pageHeader}>
                      <h2 className={css.pageTitle}>News</h2>
                      {/* <div className={css.pageControls}>
                        <button className={css.filterButton}>Filter ▼</button>
                        <button className={css.sortButton}>Newest ▼</button>
                      </div>*/}
                    </header>

                    {inProgress ? (
                      <div className={css.loading}>Loading editorials...</div>
                    ) : (
                      <>
                        <EditorialGrid
                          pageAssetsData={pageAssetsData}
                          config={config}
                        />
                        {/* <EditorialPagination />*/}
                      </>
                    )}
                  </div>
                </Main>
              </div>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

EditorialsPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: object,
  config: object,
};

const mapStateToProps = (state, ownProps) => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return {
    pageAssetsData,
    inProgress,
    error,
    config: ownProps.config,
  };
};

const EditorialsPage = compose(connect(mapStateToProps))(
  EditorialsPageComponent
);

export default EditorialsPage;
